import { type FC, useEffect, useRef, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  useStreamsListQuery,
  type StreamsItem,
} from "@/partner/entities/streams";
import { useAttachStreamEntrypointMutation } from "@/partner/entities/streamEntrypoints";
import { ContentTypeType } from "@/types/graphql/partner.generated";

const NONE = "NONE";

interface StreamEntrypointAttachFieldProps {
  entrypointId: string;
  // item.id из useStreamsListQuery — это Content.id (streams-entity list —
  // список StreamContent), как и attachStreamEntrypoint теперь ждёт.
  contentId: string | null | undefined;
}

// Список трансляций — тот же списочный запрос streams-entity, что и на
// странице «Стримы» (StreamsList.tsx), без пагинации (référence:
// Widgets.tsx/CountryAccessesFilter.tsx — тот же приём "первая страница
// вариантов селекта"). Оптимистичный выбор + откат при ошибке — тот же
// подход, что у Widgets.tsx defaultWidgetId.
export const StreamEntrypointAttachField: FC<
  StreamEntrypointAttachFieldProps
> = ({ entrypointId, contentId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data } = useStreamsListQuery({
    variables: {
      page: 0,
      rowsPerPage: 100,
      filter: { type: ContentTypeType.StreamType },
    },
  });
  const options: StreamsItem[] = data?.contents.nodes ?? [];

  const currentValue = contentId ?? NONE;
  const [value, setValue] = useState(currentValue);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  const currentValueRef = useRef(currentValue);
  currentValueRef.current = currentValue;

  const [attachStreamEntrypoint, { loading }] =
    useAttachStreamEntrypointMutation({
      refetchQueries: ["StreamEntrypoint", "StreamEntrypointsList"],
      onCompleted: () => {
        enqueueSnackbar(t("streamEntrypoints.attach.success"), {
          variant: "success",
        });
      },
      onError: (error) => {
        setValue(currentValueRef.current);
        enqueueSnackbar(error.message, { variant: "error" });
      },
    });

  const handleChange = (nextValue: string) => {
    setValue(nextValue);
    void attachStreamEntrypoint({
      variables: {
        id: entrypointId,
        contentId: nextValue === NONE ? null : nextValue,
      },
    });
  };

  return (
    <TextField
      select
      fullWidth
      variant="outlined"
      label={t("streamEntrypoints.attach.label")}
      value={value}
      disabled={loading}
      onChange={(event) => handleChange(event.target.value)}
    >
      <MenuItem value={NONE}>{t("streamEntrypoints.attach.none")}</MenuItem>
      {options.map((item) => (
        <MenuItem key={item.id} value={item.id}>
          {item.title}
        </MenuItem>
      ))}
    </TextField>
  );
};
